import React, { ReactNode } from 'react';
import { CheckBoxParam } from '../../types/CheckBoxParam';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { RequiredSymbol } from '../../partials/RequiredSymbol/RequiredSymbol';
import { Help } from '../../partials/Help/Help';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { RadioParam } from '../../types/RadioParam';
import bem from '../../../../lib/bemUtils/bem';
import { Modifier } from '../../../../lib/bemUtils/Modifier';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export enum CheckRadioForListsModifier {
  CREATE_PAGE = 'create-page',
}

export type CheckRadio = {
  type: 'checkbox' | 'radio';
  label: string;
  description?: ReactNode;
  labelInvisible?: boolean;
  helpText?: string;
  param?: CheckBoxParam<string> | RadioParam<string>;
  icon?: ReactNode;
  children?: ReactNode;
  modifier?: Modifier<CheckRadioForListsModifier>;
  ref?: React.RefObject<HTMLInputElement | null>;
} & Omit<HTMLInputProps, 'id' | 'className' | 'type'>;

/**
 * Convert Backend Checkbox Properties to HTML conform attributes.
 */
function normalizeParamsToHTMLAttributes(
  htmlInputProps: HTMLInputProps,
  param?: CheckBoxParam<string> | RadioParam<string>
) {
  return {
    name: param?.name,
    required: param?.required,
    disabled: param?.disabled,
    defaultValue: param?.value,
    defaultChecked: param?.checked,
    ...htmlInputProps,
  };
}

export function CheckRadioForLists({
  type,
  label,
  description,
  helpText,
  param,
  children,
  modifier,
  icon,
  ref,
  ...htmlInputProps
}: CheckRadio) {
  const id = useHtmlValidId();
  const htmlAttributes = normalizeParamsToHTMLAttributes(htmlInputProps, param);

  // needed for qftest
  const idPrefix = htmlAttributes.defaultValue ? `${htmlAttributes.defaultValue}-` : '';
  const checkradioId = `${idPrefix}checkradio-${id}`;
  const descriptionId = `${idPrefix}checkradio-description-text-${id}`;
  const additionalDesciriptionId = children ? `${idPrefix}checkradio-additional-description-text-${id}` : undefined;
  const helpTextId = `${idPrefix}checkradio-help-text-${id}`;

  const allDescriptionIds = () => {
    const ids = [];
    if (description) ids.push(descriptionId);
    if (children) ids.push(additionalDesciriptionId);
    if (helpText) ids.push(helpTextId);

    return ids.length > 0 ? ids.join(' ') : undefined;
  };

  return (
    <div className={bem('form2-checkradio-for-lists', modifier)}>
      <ElementContainer>
        <FieldWrapper>
          <input
            id={checkradioId}
            ref={ref}
            className="form2-checkradio-for-lists__input"
            type={type}
            aria-describedby={allDescriptionIds()}
            {...htmlAttributes}
          />
          <span className="form2-checkradio-for-lists__label-container">
            <label className="form2-checkradio-for-lists__label" htmlFor={checkradioId}>
              {icon ? icon : null}
              <span className="form2-checkradio-for-lists__label-text">
                <span>
                  {label}
                  {htmlAttributes.required && type !== 'radio' && <RequiredSymbol />}
                </span>
                {/* hidden description to avoid bloated label texts => read in screen readers on focus */}
                {description ? (
                  <span className="form2-checkradio-for-lists__label-description" id={descriptionId} aria-hidden="true">
                    {description}
                  </span>
                ) : null}
                {/* another hidden addition description => read in screen readers on focus */}
                {children ? (
                  <span id={additionalDesciriptionId} aria-hidden="true">
                    {children}
                  </span>
                ) : null}
              </span>
            </label>
          </span>
          {helpText && <Help helpText={helpText} id={helpTextId} />}
        </FieldWrapper>
      </ElementContainer>
    </div>
  );
}
